import { apiClient } from "@/services/api";
import { logError } from "@/lib/error-handling";
import { GoalProgressWithAuditDto, GoalWithAuditDto } from "@/types";
import { RelatedObjectType } from "@/enums";
import {
  CreateGoalInput,
  CreateGoalProgressInput,
  UpdateGoalInput,
} from "@/schemas";

export type CreateGoalRequest = CreateGoalInput;
export type UpdateGoalRequest = UpdateGoalInput;
export type CreateGoalProgressRequest = CreateGoalProgressInput;

/**
 * Creates a new goal by sending the provided data to the backend.
 *
 * @param data - The data required to create a new goal.
 * @returns A promise that resolves with the response data from the backend.
 * @throws Will throw an error if the request fails or if validation fails.
 */
export async function createGoal(
  accountId: string,
  data: CreateGoalRequest
): Promise<GoalWithAuditDto> {
  try {
    return await apiClient<GoalWithAuditDto>(
      `/api/accounts/${accountId}/goals`,
      {
        method: "POST",
        body: JSON.stringify(data),
      }
    );
  } catch (error) {
    logError(error, "createGoal");
    throw error;
  }
}

/**
 * Fetches all goals for a specific account by its ID.
 *
 * @param accountId - The ID of the account to fetch goals for
 * @returns A list of goals.
 * @throws An error if the request fails.
 */
export async function fetchGoalsByAccountId(
  accountId: string
): Promise<GoalWithAuditDto[]> {
  try {
    return await apiClient<GoalWithAuditDto[]>(
      `/api/accounts/${accountId}/goals`,
      {
        method: "GET",
        cache: "no-store",
      }
    );
  } catch (error) {
    logError(error, "fetchGoalsByAccountId");
    throw error;
  }
}

/**
 * Fetches a goal by its external ID and account ID.
 *
 * @param goalId - The ID of the goal to fetch
 * @returns The goal data if found, null otherwise.
 * @throws An error if the request fails.
 */
export async function fetchGoalById(
  goalId: string
): Promise<GoalWithAuditDto | null> {
  try {
    return await apiClient<GoalWithAuditDto>(`/api/goals/${goalId}`, {
      method: "GET",
      cache: "no-store",
    });
  } catch (error) {
    logError(error, "fetchGoalById");
    throw error;
  }
}

/**
 * Updates an existing goal by sending the provided data to the backend.
 *
 * @param goalId - The ID of the goal to update
 * @param data - The data required to update the goal
 * @returns A promise that resolves with the response data from the backend.
 * @throws Will throw an error if the request fails or if validation fails.
 */
export async function updateGoal(
  goalId: string,
  data: UpdateGoalRequest
): Promise<GoalWithAuditDto> {
  try {
    return await apiClient<GoalWithAuditDto>(`/api/goals/${goalId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  } catch (error) {
    logError(error, "updateGoal");
    throw error;
  }
}

/**
 * Fetches related objects for a goal by type(s).
 *
 * @param goalId - The ID of the goal
 * @param types - Array of related object types (e.g., ["ACTIVITY", "GOAL"])
 * @returns An object with arrays of related objects grouped by type.
 * @throws An error if the request fails.
 */
export async function fetchGoalRelatedObjects(
  goalId: string,
  types: RelatedObjectType[]
): Promise<Record<string, unknown[]>> {
  try {
    if (!goalId || !types.length) return {};
    const params = new URLSearchParams();
    types.forEach((t) => params.append("type", t));
    return await apiClient<Record<string, unknown[]>>(
      `/api/goals/${goalId}/related-objects?${params.toString()}`,
      {
        method: "GET",
      }
    );
  } catch (error) {
    logError(error, "fetchGoalRelatedObjects");
    throw error;
  }
}

/**
 * Creates a goal progress update by sending the provided data to the backend.
 *
 * @param goalId - The ID of the goal to update progress.
 * @param data - The data required to create a goal progress update.
 * @returns A promise that resolves with the response data from the backend.
 * @throws Will throw an error if the request fails or if validation fails.
 */
export async function createGoalProgress(
  goalId: string,
  data: CreateGoalProgressRequest
): Promise<GoalWithAuditDto> {
  try {
    return await apiClient<GoalWithAuditDto>(`/api/goals/${goalId}/progress`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  } catch (error) {
    logError(error, "createGoalProgress");
    throw error;
  }
}

/**
 * Deletes a goal by its ID.
 * @param goalId - The ID of the goal to delete
 * @returns A promise that resolves when the goal is deleted.
 * @throws An error if the request fails or if the goal is not found.
 */
export async function deleteGoal(goalId: string): Promise<void> {
  try {
    await apiClient<void>(`/api/goals/${goalId}`, {
      method: "DELETE",
    });
  } catch (error) {
    logError(error, "deleteGoal");
    throw error;
  }
}

/**
 * Fetches goal progress, optionally including related objects.
 */
export async function getGoalProgresses(
  goalId: string,
  options?: { expand?: "relatedObjects"; relatedTypes?: string[] }
): Promise<
  | GoalProgressWithAuditDto[]
  | (GoalProgressWithAuditDto & {
      relatedObjects?: Record<string, unknown[]>;
    })[]
> {
  try {
    const searchParams = new URLSearchParams();
    if (options?.expand === "relatedObjects") {
      searchParams.append("expand", "relatedObjects");
      if (options.relatedTypes && options.relatedTypes.length > 0) {
        searchParams.append("relatedTypes", options.relatedTypes.join(","));
      }
    }
    return await apiClient<GoalProgressWithAuditDto[]>(
      `/api/goals/${goalId}/progress?${searchParams.toString()}`,
      {
        method: "GET",
      }
    );
  } catch (error) {
    logError(error, "getGoalProgresses");
    throw error;
  }
}
